import { useMountEffect } from '@timelyapp/react';
import type { SplashMessageButton, SplashMessageProps } from 'components/SplashMessage';
import SplashMessage from 'components/SplashMessage';
import { HIDE_TASK_SIDEBAR } from 'constants/user';
import BetaUpsellImage from 'images/tasks/tasks-beta-upsell.png';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAccountAddsSmartly, fetchAccounts } from 'redux/modules/accounts.actions';
import {
  contactAccountManagerWithUpgradeRequest,
  contactWorkspaceAdminWithUpgradeRequest,
} from 'redux/modules/subscriptions.actions';
import { setAddOnScopeKey } from 'redux/modules/subscriptions.types';
import { saveUserProperty } from 'redux/modules/user.actions';
import {
  getAccountFeatures,
  getAccountLoading,
  getSubscriptionAddOns,
} from 'redux/selectors/accounts';
import { getUpgradePermissionsInfo } from 'redux/selectors/subscriptions';
import { getUserProperties } from 'redux/selectors/user';
import { AccountStatus } from 'types/account';
import type { ThunkDispatch } from 'types/redux';

interface Props extends SplashMessageProps {}

export const useForecastSplashMessage = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const hasTasks = useSelector(getAccountFeatures).tasks;
  // The subscription details we're interested in below are mostly off the account
  // So we want to make sure we're not in the middle of fetching it
  const isLoadingAccount = useSelector(getAccountLoading);
  const { canUserUpgradeSubscription, canContactWorkspaceOwner, canContactAccountManager } =
    useSelector(getUpgradePermissionsInfo);

  useMountEffect(() => {
    if (!hasTasks) {
      dispatch(fetchAccountAddsSmartly());
      // Changes to an add-on (e.g trial started) are not broadcast via Pusher messages, so
      // we reload accounts here to ensure we have the most recent `account.features`.
      // This is a workaround to get Intercom product tours functioning properly (ref #2 on https://github.com/Timely/project-management/issues/1019#issuecomment-900311287)
      dispatch(fetchAccounts());
    }
  });

  const contactAdmin = () => {
    const action = canContactAccountManager
      ? contactAccountManagerWithUpgradeRequest
      : contactWorkspaceAdminWithUpgradeRequest;

    dispatch(action('featureTasksRequiresUpgrade'));
  };

  const showAddOnsModal = () => dispatch(setAddOnScopeKey('Tasks'));

  const userProperties = useSelector(getUserProperties);
  const forecastAddOn = useSelector(getSubscriptionAddOns).find(addOn =>
    addOn.name.toLowerCase().includes('tasks')
  );

  const hideTaskSidebar = !!userProperties[HIDE_TASK_SIDEBAR];

  const canContactSuperior = canContactWorkspaceOwner || canContactAccountManager;

  const noTrialCTAText = canUserUpgradeSubscription
    ? 'Add Tasks to your subscription'
    : canContactAccountManager
      ? 'Contact account manager'
      : 'Ask your admin to add Tasks';

  const getTrialCTAText = canUserUpgradeSubscription
    ? 'Trial Tasks free for 14 days'
    : canContactAccountManager
      ? 'Ask your account manager to start a trial'
      : 'Ask your admin to start a trial';

  const hasTrialCTAText = canUserUpgradeSubscription
    ? 'Try for free'
    : canContactAccountManager
      ? 'Contact account manager'
      : 'Ask your admin';

  const onClick = canUserUpgradeSubscription ? showAddOnsModal : contactAdmin;
  const disabled = canUserUpgradeSubscription && !isLoadingAccount ? false : !canContactSuperior;
  const nonActionable = !canUserUpgradeSubscription && !canContactSuperior;

  const ctaButton = (): SplashMessageButton => {
    const defaults = {
      style: { minWidth: 290 },
      disabled,
      onClick,
      isLoading: isLoadingAccount,
      priority: 'xhigh' as const,
    };
    if (forecastAddOn?.status == null) {
      return {
        ...defaults,
        text: isLoadingAccount ? 'Loading...' : getTrialCTAText,
        onClick: canUserUpgradeSubscription ? showAddOnsModal : contactAdmin,
      };
    }

    if (
      forecastAddOn?.status === AccountStatus.trial_expired ||
      forecastAddOn?.status === AccountStatus.expired
    ) {
      return {
        ...defaults,
        text: isLoadingAccount ? 'Loading...' : noTrialCTAText,
      };
    }

    return {
      ...defaults,
      text: isLoadingAccount ? 'Loading...' : hasTrialCTAText,
    };
  };

  const dismissButton: SplashMessageButton = {
    text: nonActionable ? 'Got it' : 'Not right now',
    priority: nonActionable ? 'medium' : 'low',
    onClick: () => dispatch(saveUserProperty(HIDE_TASK_SIDEBAR, !hideTaskSidebar)),
  };

  const learnMoreButton: SplashMessageButton = {
    text: 'Learn more',
    priority: 'low',
    onClick: () =>
      (window.location.href = 'https://support.memory.ai/en/articles/5187690-tasks-in-timely'),
  };

  const textStates = {
    noTrialCTAText,
    getTrialCTAText,
    hasTrialCTAText,
  };

  const defaultButtons = [ctaButton(), learnMoreButton];

  const description = (
    <>
      <p>
        Tasks is the first stage of a new breed of planning tool powered by automatic time tracking.
        Acting as a layer between projects and timesheets, Tasks helps teams assign, schedule and
        log time against big pieces of work.
      </p>
      <p>
        Use this space to gain full visibility of upcoming tasks and work in progress across your
        team — managing deadlines, estimated durations, team capacity and real-time progress.
      </p>
      {nonActionable && !isLoadingAccount && <p>{ctaButton().text}.</p>}
    </>
  );

  return {
    defaultButtons,
    ctaButton: !canUserUpgradeSubscription && !canContactSuperior ? null : ctaButton(),
    learnMoreButton,
    dismissButton,
    handleCTAClick: onClick,
    isDisabled: disabled,
    textStates,
    description,
  };
};

const ForecastSplashMessage: React.FC<Props> = props => {
  const { defaultButtons, description } = useForecastSplashMessage();

  return (
    <SplashMessage
      buttons={defaultButtons}
      description={description}
      {...props}
      horizontal={props.size !== 'small'}
    />
  );
};

ForecastSplashMessage.defaultProps = {
  centered: false,
  image: BetaUpsellImage,
  subtext: '',
  title: 'Your team hub for managing work in Timely',
};

export default ForecastSplashMessage;
